/**
 * Current-year family registration status for the logged-in user.
 * Used by FamilyRegistrationGate so users who haven't completed are sent to the form.
 * Prefers GET /family-registration/status when API is available; falls back to localStorage when API is unset or fails.
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { familyRegistrationApi } from '@services/familyRegistrationApi';
import {
  getCurrentSchoolYear,
  getFamilyRegistrationComplete,
} from '@utils/familyRegistrationStorage';

export interface FamilyRegistrationStatus {
  /** True if the user has completed family registration for the current school year. */
  isComplete: boolean;
  isLoading: boolean;
  /** Optional label for the current school year (e.g. "2025-2026"). */
  schoolYearLabel?: string;
  /** Call to refetch status (e.g. after submitting the form). */
  refetch: () => void;
}

interface ResolvedStatus {
  isComplete: boolean;
  isLoading: boolean;
  schoolYearLabel: string | undefined;
}

/**
 * Status that can be known without the API: signed out, or signed in without a token
 * (localStorage fallback). `null` means the API has to be asked.
 */
function resolveLocalStatus(userId: string | undefined, token: string | null): ResolvedStatus | null {
  if (!userId) {
    return { isComplete: false, isLoading: false, schoolYearLabel: undefined };
  }
  if (!token) {
    return {
      isComplete: getFamilyRegistrationComplete(userId),
      isLoading: false,
      schoolYearLabel: getCurrentSchoolYear(),
    };
  }
  return null;
}

/**
 * GET /family-registration/status. Resolves to `null` when the response has no data; on failure
 * resolves to the localStorage fallback. Never rejects.
 */
function fetchRemoteStatus(
  token: string,
  userId: string
): Promise<Pick<ResolvedStatus, 'isComplete' | 'schoolYearLabel'> | null> {
  return familyRegistrationApi.getStatus(token).then(
    (res) =>
      res.data ? { isComplete: res.data.isComplete, schoolYearLabel: res.data.schoolYear } : null,
    () => ({
      schoolYearLabel: getCurrentSchoolYear(),
      isComplete: getFamilyRegistrationComplete(userId),
    })
  );
}

/**
 * Returns whether the current user has completed the annual family registration for the current school year.
 * Uses GET /family-registration/status when API is configured; otherwise localStorage.
 */
export function useFamilyRegistrationStatus(): FamilyRegistrationStatus {
  const { user, token } = useAuth();
  const userId = user?.userId;
  const [initialLocal] = useState(() => resolveLocalStatus(userId, token));
  const [isComplete, setIsComplete] = useState(initialLocal?.isComplete ?? false);
  const [isLoading, setIsLoading] = useState(initialLocal?.isLoading ?? true);
  const [schoolYearLabel, setSchoolYearLabel] = useState<string | undefined>(
    initialLocal?.schoolYearLabel
  );

  const applyRemoteStatus = useCallback(
    (remote: Pick<ResolvedStatus, 'isComplete' | 'schoolYearLabel'> | null) => {
      if (remote) {
        setIsComplete(remote.isComplete);
        setSchoolYearLabel(remote.schoolYearLabel);
      }
      setIsLoading(false);
    },
    []
  );

  // When the user or token changes, apply the status that needs no request right away
  // (adjust state during render); the effect below asks the API when there is a token.
  const [prevInputs, setPrevInputs] = useState({ userId, token });
  if (prevInputs.userId !== userId || prevInputs.token !== token) {
    setPrevInputs({ userId, token });
    const local = resolveLocalStatus(userId, token);
    if (local) {
      setIsComplete(local.isComplete);
      setIsLoading(local.isLoading);
      setSchoolYearLabel(local.schoolYearLabel);
    }
  }

  useEffect(() => {
    if (!userId || !token) return;
    void fetchRemoteStatus(token, userId).then(applyRemoteStatus);
  }, [userId, token, applyRemoteStatus]);

  const fetchStatus = useCallback(() => {
    if (!userId || !token) {
      const local = resolveLocalStatus(userId, token);
      if (local) {
        setIsComplete(local.isComplete);
        setIsLoading(local.isLoading);
        setSchoolYearLabel(local.schoolYearLabel);
      }
      return;
    }
    void fetchRemoteStatus(token, userId).then(applyRemoteStatus);
  }, [userId, token, applyRemoteStatus]);

  return { isComplete, isLoading, schoolYearLabel, refetch: fetchStatus };
}
